import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  BookOpen,
  CalendarClock,
  Calendar,
  Compass,
  FolderKanban,
  Layers,
  MessageSquare,
  Package,
  Plus,
  Search,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChatContext } from "./providers/ChatProvider";
import type { ModelCatalog } from "../../chat/client";

/** A single actionable entry in the palette. */
interface Command {
  id: string;
  label: string;
  /** Extra text matched by the filter but not shown as the title. */
  keywords?: string;
  /** Group heading the command renders under. */
  group: "Actions" | "Go to" | "Models" | "Chats";
  icon: React.ReactNode;
  /** Secondary line (e.g. a chat's relative date, a model's note). */
  hint?: string;
  run: () => void;
}

/**
 * Global command palette (Cmd/Ctrl+K), in the spirit of ChatGPT/Linear/VS Code.
 * Opens from anywhere on the chat surface and lets the user:
 *   - jump to / search their chats
 *   - start a new chat
 *   - switch the active model
 *   - navigate to the workspace pages
 *
 * Mounted once inside the ChatProvider so it can reach sessions, the model
 * catalog, and the active-session setters via context. Keyboard-first: ↑/↓ to
 * move, Enter to run, Esc to close.
 */
/** Dispatch on `window` to open the command palette from anywhere (e.g. the
 *  sidebar search button) — same surface as Cmd/Ctrl+K. */
export const COMMAND_MENU_OPEN_EVENT = "artie:open-command-menu";

export function CommandMenu() {
  const navigate = useNavigate();
  const { client, sessions, setSessionId, selectedModel, setSelectedModel, features } =
    useChatContext();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [catalog, setCatalog] = useState<ModelCatalog | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Global Cmd/Ctrl+K toggle + a custom "open" event so UI affordances (the
  // sidebar search button) can open the same palette. Registered once.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(COMMAND_MENU_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(COMMAND_MENU_OPEN_EVENT, onOpen);
    };
  }, []);

  // Lazy-load the model catalog the first time the palette opens.
  useEffect(() => {
    if (!open || catalog) return;
    let cancelled = false;
    client
      .listModels()
      .then((c) => { if (!cancelled) setCatalog(c); })
      .catch(() => {/* models stay absent — palette still works */});
    return () => { cancelled = true; };
  }, [open, catalog, client]);

  // Reset transient state each time the palette opens.
  useEffect(() => {
    if (open) { setQuery(""); setHighlight(0); }
  }, [open]);

  const close = () => setOpen(false);

  const commands = useMemo<Command[]>(() => {
    const out: Command[] = [];

    // Actions
    out.push({
      id: "new-chat",
      label: "New chat",
      keywords: "start fresh conversation compose",
      group: "Actions",
      icon: <Plus size={16} />,
      run: () => { setSessionId(null); navigate("/workspace"); },
    });

    // Navigation (mirrors the sidebar nav; Projects gated by feature flag)
    const nav: { href: string; label: string; icon: React.ReactNode }[] = [
      { href: "/workspace", label: "Chat", icon: <MessageSquare size={16} /> },
      ...(features.projects
        ? [{ href: "/workspace/projects", label: "Projects", icon: <FolderKanban size={16} /> }]
        : []),
      { href: "/workspace/skills", label: "Skills", icon: <Zap size={16} /> },
      { href: "/workspace/knowledge", label: "Knowledge", icon: <BookOpen size={16} /> },
      { href: "/workspace/meetings", label: "Meetings", icon: <CalendarClock size={16} /> },
      { href: "/workspace/plugins", label: "Plugins", icon: <Package size={16} /> },
      { href: "/workspace/schedules", label: "Schedules", icon: <Calendar size={16} /> },
      { href: "/workspace/batches", label: "Batches", icon: <Layers size={16} /> },
    ];
    for (const n of nav) {
      out.push({
        id: `nav:${n.href}`,
        label: n.label,
        group: "Go to",
        icon: n.icon,
        run: () => navigate(n.href),
      });
    }

    // Models (only when there's a real choice)
    if (catalog && catalog.models.length > 1) {
      for (const m of catalog.models) {
        out.push({
          id: `model:${m.id}`,
          label: `Use model: ${m.label}`,
          keywords: m.id,
          group: "Models",
          icon: <Compass size={16} />,
          hint: m.id === selectedModel ? "Current" : undefined,
          run: () => setSelectedModel(m.id),
        });
      }
    }

    // Chats (cap so the list stays scannable; filter narrows further)
    for (const s of sessions.sessions.slice(0, 50)) {
      const title = (s.title && s.title.trim()) || "Untitled chat";
      out.push({
        id: `chat:${s.id}`,
        label: title,
        group: "Chats",
        icon: <MessageSquare size={16} />,
        run: () => { setSessionId(s.id); navigate("/workspace"); },
      });
    }

    return out;
  }, [catalog, features.projects, navigate, selectedModel, sessions.sessions, setSelectedModel, setSessionId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.keywords ?? ""} ${c.group}`.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Keep the highlight in range as the filter narrows.
  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0);
  }, [filtered.length, highlight]);

  const runAt = (i: number) => {
    const cmd = filtered[i];
    if (!cmd) return;
    cmd.run();
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => (filtered.length ? (i + 1) % filtered.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => (filtered.length ? (i - 1 + filtered.length) % filtered.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runAt(highlight);
    }
  };

  // Scroll the highlighted row into view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-index="${highlight}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  // Render rows with group headings injected when the group changes.
  let lastGroup: string | null = null;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { borderRadius: "20px", overflow: "hidden", mt: "-10vh" } } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 }}>
        <Search size={18} style={{ opacity: 0.6 }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
          onKeyDown={onKeyDown}
          placeholder="Search chats, switch model, jump to…"
          slotProps={{ input: { "aria-label": "command menu search" } }}
          sx={{ fontSize: 15 }}
        />
      </Box>
      <List ref={listRef} dense sx={{ maxHeight: "52vh", overflowY: "auto", py: 0.5 }}>
        {filtered.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">No matches</Typography>
          </Box>
        )}
        {filtered.map((c, i) => {
          const header = c.group !== lastGroup ? c.group : null;
          lastGroup = c.group;
          return (
            <React.Fragment key={c.id}>
              {header && (
                <Typography
                  variant="overline"
                  sx={{ display: "block", px: 2, pt: 1, pb: 0.25, color: "text.secondary", letterSpacing: "0.08em" }}
                >
                  {header}
                </Typography>
              )}
              <ListItemButton
                data-cmd-index={i}
                selected={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => runAt(i)}
                sx={{ mx: 1, borderRadius: 1.5, py: 0.6 }}
              >
                <ListItemIcon sx={{ minWidth: 30, color: "inherit" }}>{c.icon}</ListItemIcon>
                <ListItemText
                  primary={c.label}
                  secondary={c.hint}
                  slotProps={{ primary: { variant: "body2", noWrap: true }, secondary: { variant: "caption" } }}
                />
              </ListItemButton>
            </React.Fragment>
          );
        })}
      </List>
    </Dialog>
  );
}
